import React, { useEffect, useState } from "react";
import { useTranslate, useCreate } from "@refinedev/core";
import { useSelect } from "@refinedev/antd";
import { Modal, Form, Input, Select, Space, Button, message } from "antd";
import { SaveOutlined } from "@ant-design/icons";

type ClientCreateModalProps = {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
};

export const ClientCreateModal: React.FC<ClientCreateModalProps> = ({
  open,
  onCancel,
  onSuccess,
}) => {
  const _t = useTranslate();
  const [form] = Form.useForm();

  const { mutate: createClient, isLoading: isCreating } = useCreate();
  const [secretModalOpen, setSecretModalOpen] = useState(false);
  const [generatedSecret, setGeneratedSecret] = useState<string | null>(null);

  const { selectProps: tenantSelectProps } = useSelect({
    resource: "tenants",
    optionLabel: "name",
    optionValue: "id",
    queryOptions: {
      enabled: open,
    },
  });

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleFinish = (values: any) => {
    createClient(
      {
        resource: "clients",
        values: {
          name: values.name,
          scopes: values.scopes,
          ...(values.tenantId != null && values.tenantId !== undefined
            ? { tenantId: values.tenantId }
            : {}),
        },
      },
      {
        onSuccess: (response) => {
          const created: any = (response as any)?.data ?? response;
          if (created?.secret) {
            setGeneratedSecret(created.secret);
            setSecretModalOpen(true);
          }

          form.resetFields();
          onCancel();
          onSuccess?.();
        },
      },
    );
  };

  return (
    <>
      <Modal
        title={_t("clients.titles.create")}
        open={open}
        onCancel={onCancel}
        width={520}
        destroyOnClose
        footer={
          <Space>
            <Button onClick={onCancel}>{_t("buttons.cancel")}</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={isCreating}
              onClick={() => form.submit()}
            >
              {_t("buttons.save")}
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          className="!mt-2"
        >
          <Form.Item
            label={_t("clients.fields.tenant")}
            name="tenantId"
          >
            <Select
              {...tenantSelectProps}
              placeholder={_t("clients.fields.tenant")}
              allowClear
              className="w-full"
            />
          </Form.Item>
          <Form.Item
            label={_t("clients.fields.name")}
            name="name"
            rules={[
              {
                required: true,
                message: _t("fields.errors.required"),
              },
            ]}
          >
            <Input placeholder={_t("clients.fields.name")} />
          </Form.Item>
          <Form.Item
            label={_t("clients.fields.scopes")}
            name="scopes"
            rules={[
              {
                required: true,
                message: _t("fields.errors.required"),
              },
            ]}
          >
            <Select
              mode="tags"
              placeholder={_t("clients.fields.scopes")}
              tokenSeparators={[","]}
              className="w-full"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={_t("clients.messages.rotateSuccessTitle")}
        open={secretModalOpen}
        onCancel={() => {
          setSecretModalOpen(false);
          setGeneratedSecret(null);
        }}
        footer={[
          <Button
            key="copy-generated"
            icon={<SaveOutlined />}
            onClick={() => {
              if (generatedSecret) {
                navigator.clipboard.writeText(generatedSecret);
                message.success(_t("clients.messages.copied"));
              }
            }}
          >
            {_t("clients.actions.copySecret")}
          </Button>,
          <Button
            key="close-generated"
            type="primary"
            onClick={() => {
              setSecretModalOpen(false);
              setGeneratedSecret(null);
            }}
          >
            {_t("buttons.close")}
          </Button>,
        ]}
      >
        <p className="mb-2">{_t("clients.messages.rotateSuccessBody")}</p>
        <Input.Password
          readOnly
          value={generatedSecret ?? ""}
          className="font-mono"
        />
      </Modal>
    </>
  );
};
